import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import MenuButton from "../MenuButton";
import { useMap } from "react-leaflet";

import * as S from "./styles";
import { mobileBreakpoint, shortScreenBreakpoint } from "../../styles/breakpoints";
import sampleLogoBackground from "../../utils/logoBackground";

function FloatingMenu({
  logo,
  locationsContext,
  getIconInline,
  sideMenu = false,
}) {
  const { title, categories, toggleCategoryVisibility, center, locationsConfig } =
    useContext(locationsContext);
  const [buttons, setButtons] = useState([]);
  const [compact, setCompact] = useState(false);
  const [logoColors, setLogoColors] = useState({});
  const wrapperRef = useRef(null);
  const buttonsRef = useRef(null);
  const addressRef = useRef(null);
  const naturalWidth = useRef(0);
  const map = useMap();

  useEffect(() => {
    setButtons(Object.keys(categories));
    const buttonsWithData = Object.keys(categories).map(
      (item) => categories[item]
    );
    setButtons(buttonsWithData);
  }, []);

  // Cada mapa tem um número diferente de categorias, então não dá para
  // escolher um breakpoint fixo: medimos a largura natural da barra (ela não
  // depende do tamanho da tela) e compactamos quando ela não cabe.
  useLayoutEffect(() => {
    const nav = wrapperRef.current;
    const strip = buttonsRef.current;
    if (!nav || !strip || !buttons.length) return;

    if (!naturalWidth.current) {
      // Mede com as travas de responsividade suspensas, senão a própria
      // quebra de linha esconde o quanto a barra realmente precisa. A linha
      // de endereço é elástica (width: 100%), então também precisa ser
      // medida pelo conteúdo.
      const address = addressRef.current;
      const saved = [nav.style.width, nav.style.maxWidth, nav.style.flexWrap];
      const savedStrip = strip.style.flexWrap;
      const savedAddress = address && [
        address.style.width,
        address.style.flex,
        address.style.whiteSpace,
      ];

      nav.style.width = "max-content";
      nav.style.maxWidth = "none";
      nav.style.flexWrap = "nowrap";
      strip.style.flexWrap = "nowrap";
      if (address) {
        address.style.width = "auto";
        address.style.flex = "0 0 auto";
        address.style.whiteSpace = "nowrap";
      }

      naturalWidth.current = nav.getBoundingClientRect().width;

      [nav.style.width, nav.style.maxWidth, nav.style.flexWrap] = saved;
      strip.style.flexWrap = savedStrip;
      if (address) {
        [address.style.width, address.style.flex, address.style.whiteSpace] =
          savedAddress;
      }
    }

    const update = () =>
      setCompact(
        naturalWidth.current > window.innerWidth ||
          window.innerWidth <= mobileBreakpoint ||
          window.innerHeight <= shortScreenBreakpoint
      );

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [buttons]);

  // Na barra compacta a logo preenche o bloco, e o que sobra do lado ou de
  // cima aparece com a cor do empreendimento — um degrau visível contra o
  // fundo da própria imagem. Pintamos o bloco com a cor lida da logo.
  useEffect(() => {
    const nav = wrapperRef.current;
    if (!nav) return;

    const cleanups = [];
    nav.querySelectorAll("[data-logo-block]").forEach((block, index) => {
      const image = block.querySelector("img");
      if (!image) return;

      const apply = () => {
        const color = sampleLogoBackground(image);
        if (!color) return;
        setLogoColors((current) =>
          current[index] === color ? current : { ...current, [index]: color }
        );
      };

      if (image.complete && image.naturalWidth) {
        apply();
        return;
      }
      image.addEventListener("load", apply);
      cleanups.push(() => image.removeEventListener("load", apply));
    });

    return () => cleanups.forEach((remove) => remove());
  }, [buttons]);

  // O CSS do mapa precisa saber do formato compacto para tirar os controles
  // de zoom e a atribuição de baixo da barra.
  useLayoutEffect(() => {
    document.body.classList.toggle("compact-menu", compact);
    return () => document.body.classList.remove("compact-menu");
  }, [compact]);

  return (
    <S.Wrapper
      ref={wrapperRef}
      sideMenu={sideMenu}
      compact={compact}
      style={{ zIndex: 1000 }}
      backgroundColor={center.secondaryColor}
    >
      {locationsConfig?.resetPoints.map((point, index) => (
        <S.ResetWrapper
          key={`${index}_${point.point}`}
          data-logo-block
          onClick={() => map.flyTo(point.latlng, center.zoom)}
          backgroundColor={
            (compact && logoColors[index]) || center.primaryColor
          }
        >
          <S.LogoTitle>
            <S.WrapperLogo>
              { point?.image ? '' : logo }
            </S.WrapperLogo>
            {!!point.name && (
              <S.WrapperTitle>
                <S.PointName>{point.name}</S.PointName>
              </S.WrapperTitle>
            )}
          </S.LogoTitle>
        </S.ResetWrapper>
      ))}

      {!!locationsConfig?.asdressInfo && (
        <S.LocationTextWrapper ref={addressRef}>
          <small>{locationsConfig.asdressInfo}</small>
        </S.LocationTextWrapper>
      )}

      {!locationsConfig?.resetPoints && (
        <S.ResetWrapper
          data-logo-block
          onClick={() => map.flyTo(center.latlng, center.zoom)}
          backgroundColor={(compact && logoColors[0]) || center.primaryColor}
        >
          { logo }
        </S.ResetWrapper>
      )}
      <S.ButtonsWrapper
        ref={buttonsRef}
        className={ `${ locationsConfig?.style }` }
        sideMenu={sideMenu}
      >
        {buttons.map((item, index) => {
          if (item.onMenu)
            return (
              <MenuButton
                key={item.id + index}
                label={item.label}
                image={getIconInline[item.icon]}
                action={() => toggleCategoryVisibility(item.id)}
                active={categories[item.id].active}
                compact={compact}
              />
            );
          return null;
        })}
      </S.ButtonsWrapper>
    </S.Wrapper>
  );
}

export default FloatingMenu;
